/**
 * WebGL detection utility
 * Checks if WebGL is supported in the current browser/environment
 */

export const isWebGLSupported = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    if (!canvas.getContext) {
      return false;
    }

    const gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');

    // Some browsers return valid contexts that do not pass reliable instanceof checks.
    return gl !== null;
  } catch (error) {
    return false;
  }
};

export const getWebGLErrorInfo = (): string => {
  if (!isWebGLSupported()) {
    return 'WebGL is not supported in this browser or environment';
  }
  return 'WebGL is supported but failed to initialize';
};

let webGL2Support: boolean | null = null;

/**
 * Whether the browser can create a WebGL2 context. Probed once and
 * remembered: every probe costs a context, and the answer cannot change
 * within a page load. The probe's context is released straight away (the
 * water and the map each need one of their own, and browsers cap how many
 * may live at once). False outside a browser.
 */
export const isWebGL2Supported = (): boolean => {
  if (webGL2Support === null) {
    try {
      const context =
        typeof document !== 'undefined'
          ? document.createElement('canvas').getContext('webgl2')
          : null;

      webGL2Support = context !== null;
      context?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      webGL2Support ??= false;
    }
  }

  return webGL2Support;
};

/** Forgets the probe's answer (tests). */
export const resetWebGL2SupportForTests = (): void => {
  webGL2Support = null;
};
